import { Box, Card, CardHeader, Divider, Grid, Chip, Table, TableBody, TableCell, TableRow, Typography, 
  Stack } from "@mui/material";
import { ListContextProvider, ReduxState, ResourceComponentPropsWithId, ShowBase, 
  useListContext, useListController, useShowContext } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import Scrollbar from "@/components/Scrollbar";
import { Content } from "@/components/content/Content";
import { FunctionField, TextField } from "@/components/field";
import DateField2 from "@/components/field/DateField2";
import SizeTextField from "@/components/field/SizeTextField";
import { Datagrid } from "@/components/list/datagrid";
import { RecPatch } from "@/dataProvider/tools";
import { PlainFileScanInstanceResponseDto, PlainFileScanResponseDto, FileTagResponseDto, DataOwnerGroupByFileId } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import { decodeHashid } from "@/utils/hashid";
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "@/components/securefiles/FileCataAmbientDataProvider";
import ClassificationLabel from "@/components/governance/labels/ClassificationLabel";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { crudGetManyEx } from "@/actions/crudFetchEx";
import { useDispatch, useSelector } from "react-redux";



const PlainFileScanDetail: FC<ResourceComponentPropsWithId> = ({
  id: hId,
  resource,
  options,
  history,
}) => {
  useResourceRegister({ name: 'secure-files-pfs-instances' })
  const id = decodeHashid(hId || '')
  const { enqueueSnackbar } = useSnackbar()
  const onFailure = useCallback((err: any) => {
    const errMsg = retrieveAxiosErr(err)
    enqueueSnackbar(`Fetch File Detail failed: ${errMsg}`, { variant: 'error' })
    if (history && history.length > 0) {
      history.goBack()
    }
  }, [enqueueSnackbar, history])
  
  return (
    <ShowBase resource={resource} id={id} basePath="" {...{onFailure}}>
      <ListContextProvider value={useListController<PlainFileScanInstanceResponseDto&RecPatch>({ resource: id ? 'secure-files-pfs-instances' : 'noop', basePath: '', filter: {id}})}>
        <FileCataAmbientDataContextProvider>
          <PlainFileScanDetailBody {...{id, resource, options}} />
        </FileCataAmbientDataContextProvider>
      </ListContextProvider>
    </ShowBase>
  )
}

const PlainFileScanDetailBody: FC<OdResourcePageProps> = ({
  options,
}) => {
  const { record } = useShowContext<PlainFileScanResponseDto&RecPatch>()
  const { classifications, tags } = useFileCataAmbientDataContext()
  const [clss, fileTags] = useMemo(() => {
    const clss = classifications.find(c => c.secondaryClassificationId === record?.secondaryClassificationId)
    const fileTags : FileTagResponseDto[] = (record?.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x) as any
    return [clss, fileTags] as const
  }, [record, classifications, tags])
  const listCtx = useListContext()
  const { total, setPage } = listCtx
  const setPageRef = useRef(setPage)
  setPageRef.current = setPage
  useEffect(() => {
    setTimeout(() => setPageRef.current(1))
  }, [record?.entryId])
  const dataownergroupName = usePrepareDataOwnerGroupData(record?.entryId ?? -1)

  return (
    <Content 
      pageName="File Instances" 
      resourceName="File Instances" 
      breadcrumbs={options?.breadcrumbs}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card >
            <CardHeader title="Basic Info" />
            <Divider />
            <Table sx={{ '& td': { py: 1.25 }, '& p.filename': { maxWidth:700, wordWrap:'break-word'}}}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 150 }}>
                    <Typography variant="subtitle2">File Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className="filename">
                      {record?.fileName}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Instances</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{total ?? 0}</Typography>
                  </TableCell>
                </TableRow>
                {Boolean(clss) && 
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Classification</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction={"row"}>
                        <Box sx={{ width:'50%' }}>
                          <ClassificationLabel variant={clss?.primaryClassification!} text={clss?.name!} />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>}
                {dataownergroupName &&
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Data Owner Group</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dataownergroupName}</Typography>
                    </TableCell>
                  </TableRow>}
                {Boolean(fileTags.length) &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Tags</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ '& p': {mb: 0.5}}}>
                        {fileTags.map(t => 
                          <Typography key={t.tagId!} variant="caption" fontStyle="italic" paragraph>{t.name}</Typography>)}
                      </Box>
                    </TableCell>
                  </TableRow>}
                {record?.documentType &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Document Type</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ '& p': {mb: 0.5}}}>
                        <Chip label={record.documentType} color="success" variant="outlined" />
                      </Box>
                    </TableCell>
                  </TableRow>}
                {record?.infoTypesArr && record.infoTypesArr.length > 0 &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Info Types</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" spacing={2} 
                        sx={{ 
                          ml: -2, mt: -0.5,
                          '& p': {mb: 0.5},
                          '& div.MuiChip-root': { mt: '4px!important'},
                          '& div.MuiChip-root:nth-of-type(1)': { ml: 2 },
                        }}
                      >
                        {record.infoTypesArr.map((x) => (
                          <Chip label={x} size="small" color="secondary" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} >
          <Card>
            <CardHeader title="Instances" />
            <Divider />
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  {...listCtx}
                >
                  {/* <TextField label="User" source="userName" sortable={false}/> */}
                  <TextField label="Computer" source="machine" sortable={false}/>
                  <FunctionField label="Location" source="path" sortable={false}
                    render={(d?:PlainFileScanInstanceResponseDto&RecPatch) => 
                      <Typography variant="caption" color="secondary" sx={{maxWidth:450,display:'inline-block',wordWrap:'break-word'}}>
                        <code>{d?.filePath}</code>
                      </Typography>
                    }
                  />
                  <SizeTextField label="Size" source="size" numericSource="size" emptyText="N/A" sortable={false}/>
                  <DateField2 source="lastModified" label="Last Modified Time" showTime sortable={false}/>
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default PlainFileScanDetail;

function usePrepareDataOwnerGroupData (fileId: number) {
  const resGetMny = 'secure-files/plainfile/data-owner-group'
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetManyEx(resGetMny, [fileId]))
  }, [dispatch, fileId])

  return useSelector<ReduxState>(store => {
    const dog : DataOwnerGroupByFileId|undefined = store.admin.resources[resGetMny]?.data[fileId] as any
    return dog?.name
  })
}
